import { LoadingButton } from '@mui/lab';
import { Alert, Button, Collapse, Divider, Grid, TextField } from '@mui/material';
import { FormikValues } from 'formik';
import { isString } from 'lodash';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageTabs } from 'src/hooks/useLanguageTabs';
import { useEdit } from '../../../hooks';
import {
  clearDirection,
  clearDirectionRequest,
  createDirection,
  editDirection,
  forceGetDirectionWarehouse,
  getDirection,
} from '../../../thunks/direction-thunks';
import { OrganizationsSelect } from '../../organizations';
import {
  DirectionsEditValidationForm,
  FIELD_DESCRIPTION,
  FIELD_DESCRIPTION_OZ,
  FIELD_DESCRIPTION_RU,
  FIELD_DESCRIPTION_UZ,
  FIELD_ORGANIZATION,
  FIELD_TITLE,
  FIELD_TITLE_OZ,
  FIELD_TITLE_RU,
  FIELD_TITLE_UZ,
} from './directions-edit-schema';

export interface DirectionsEditFormProps {
  id?: string | number;
  onCancel?: () => void;
  onSubmit: (values: FormikValues) => void;
}

export const DirectionsEditForm: ComponentType<DirectionsEditFormProps> = ({
  id,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { tabLang, renderTabsButtons } = useLanguageTabs();

  const { isLoading, isFetching, error, formik, handleFieldChange, hasError, getErrorMessage } =
    useEdit({
      id,
      entity: 'direction',
      initialValues: {
        [FIELD_TITLE]: {
          ru: '',
          uz: '',
          oz: '',
        },
        [FIELD_DESCRIPTION]: {
          ru: '',
          uz: '',
          oz: '',
        },
        [FIELD_ORGANIZATION]: '',
      },
      validationSchema: DirectionsEditValidationForm,
      onGetWareHouse: forceGetDirectionWarehouse,
      onGetOne: getDirection,
      onClearOne: clearDirection,
      onClear: clearDirectionRequest,
      onCreate: createDirection,
      onEdit: editDirection,
      onSubmit: onSubmit,
    });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container mb={3} direction="column">
        <Grid item xs={12} mb={2}>
          {renderTabsButtons()}
        </Grid>
        {tabLang === 'ru' && (
          <>
            <Grid item>
              <TextField
                {...formik.getFieldProps(`${FIELD_TITLE_RU}`)}
                onChange={(e: any) => handleFieldChange(`${FIELD_TITLE_RU}`, e)}
                error={hasError(`${FIELD_TITLE_RU}`)}
                helperText={getErrorMessage(`${FIELD_TITLE_RU}`)}
                label={`${t('directions.title.label', 'Название направления')} (${t(
                  'russian',
                  'русский'
                )})`}
                placeholder={t('directions.title.placeholder', 'Введите название направления')}
                disabled={isLoading || isFetching}
                fullWidth
              />
            </Grid>
            <Grid item>
              <TextField
                {...formik.getFieldProps(`${FIELD_DESCRIPTION_RU}`)}
                onChange={(e: any) => handleFieldChange(`${FIELD_DESCRIPTION_RU}`, e)}
                error={hasError(`${FIELD_DESCRIPTION_RU}`)}
                helperText={getErrorMessage(`${FIELD_DESCRIPTION_RU}`)}
                label={`${t('directions.description.label', 'Описание направления')} (${t(
                  'russian',
                  'русский'
                )})`}
                placeholder={t(
                  'directions.description.placeholder',
                  'Введите описание направления'
                )}
                disabled={isLoading || isFetching}
                rows={4}
                multiline
                fullWidth
              />
            </Grid>
          </>
        )}
        {tabLang === 'uz' && (
          <>
            <Grid item>
              <TextField
                {...formik.getFieldProps(`${FIELD_TITLE_UZ}`)}
                onChange={(e: any) => handleFieldChange(`${FIELD_TITLE_UZ}`, e)}
                error={hasError(`${FIELD_TITLE_UZ}`)}
                helperText={getErrorMessage(`${FIELD_TITLE_UZ}`)}
                label={`${t('directions.title.label', 'Название направления')} (${t(
                  'uzbek',
                  'узбекский'
                )})`}
                placeholder={t('directions.title.placeholder', 'Введите название направления')}
                disabled={isLoading || isFetching}
                fullWidth
              />
            </Grid>
            <Grid item>
              <TextField
                {...formik.getFieldProps(`${FIELD_DESCRIPTION_UZ}`)}
                onChange={(e: any) => handleFieldChange(`${FIELD_DESCRIPTION_UZ}`, e)}
                error={hasError(`${FIELD_DESCRIPTION_UZ}`)}
                helperText={getErrorMessage(`${FIELD_DESCRIPTION_UZ}`)}
                label={`${t('directions.description.label', 'Описание направления')} (${t(
                  'uzbek',
                  'узбекский'
                )})`}
                placeholder={t(
                  'directions.description.placeholder',
                  'Введите описание направления'
                )}
                disabled={isLoading || isFetching}
                rows={4}
                multiline
                fullWidth
              />
            </Grid>
          </>
        )}
        {tabLang === 'oz' && (
          <>
            <Grid item>
              <TextField
                {...formik.getFieldProps(`${FIELD_TITLE_OZ}`)}
                onChange={(e: any) => handleFieldChange(`${FIELD_TITLE_OZ}`, e)}
                error={hasError(`${FIELD_TITLE_OZ}`)}
                helperText={getErrorMessage(`${FIELD_TITLE_OZ}`)}
                label={`${t('directions.title.label', 'Название направления')} (${t('ozbek')})`}
                placeholder={t('directions.title.placeholder', 'Введите название направления')}
                disabled={isLoading || isFetching}
                fullWidth
              />
            </Grid>
            <Grid item>
              <TextField
                {...formik.getFieldProps(`${FIELD_DESCRIPTION_OZ}`)}
                onChange={(e: any) => handleFieldChange(`${FIELD_DESCRIPTION_OZ}`, e)}
                error={hasError(`${FIELD_DESCRIPTION_OZ}`)}
                helperText={getErrorMessage(`${FIELD_DESCRIPTION_OZ}`)}
                label={`${t('directions.description.label', 'Описание направления')} (${t(
                  'ozbek'
                )})`}
                placeholder={t(
                  'directions.description.placeholder',
                  'Введите описание направления'
                )}
                disabled={isLoading || isFetching}
                rows={4}
                multiline
                fullWidth
              />
            </Grid>
          </>
        )}

        <Grid item>
          <OrganizationsSelect
            {...formik.getFieldProps(FIELD_ORGANIZATION)}
            onChange={(e: any) => handleFieldChange(FIELD_ORGANIZATION, e)}
            error={hasError(FIELD_ORGANIZATION)}
            helperText={getErrorMessage(FIELD_ORGANIZATION)}
            label={t('organizations.name', 'Название организации')}
            placeholder={t('organizations.name.placeholder', 'Введите название организации')}
            disabled={isLoading || isFetching}
            fullWidth
          />
        </Grid>
      </Grid>
      {isString(error) && (
        <Collapse in={!!error}>
          <Alert severity="error">{error}</Alert>
        </Collapse>
      )}
      <Divider sx={{ mt: 3, mb: 4 }} />
      <Grid container columnSpacing={2} justifyContent="space-between">
        <Grid item>
          {onCancel && (
            <Button onClick={onCancel} variant="outlined" size="large" disabled={isLoading}>
              {t('cancel', 'Отмена')}
            </Button>
          )}
        </Grid>
        <Grid item>
          <LoadingButton
            loading={isLoading || isFetching}
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
          >
            {t('action.save', 'Сохранить')}
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  );
};
